import { useCallback, useEffect, useMemo, useState } from 'react';
import { collection, deleteDoc, doc, type DocumentData, getDoc, getDocFromServer, limit, onSnapshot, orderBy, query, type QueryDocumentSnapshot, runTransaction, serverTimestamp, setDoc, updateDoc, where, writeBatch } from 'firebase/firestore';
import { updateProfile as updateAuthProfile } from 'firebase/auth';
import type { ChatItem, ChatMessage, TonyteActivity, TonyteUser, VibeRequest } from '../types';
import { auth, DEFAULT_AVATAR, DEFAULT_COVER, firebase, firestore, FIRESTORE_COLLECTIONS } from '../firebase';
import { getUserDisplayName, getUserProfile, invalidateUserProfile, publicBio } from '../utils/userProfiles';
import { isPlanActivity } from '../utils/activityKind';
import { normalizeUserRole } from '../utils/roles';
import { canAccessCity, normalizeCityKey } from '../utils/cities';
import { subscribeShared, useSharedQueryDocs } from '../utils/sharedFirestoreSubscription';
import { calculateAge } from '../utils/age';
import { getInstalledAppVersion } from '../utils/appVersion';
import { LANGUAGE_LOCALES, useI18n } from '../i18n';
import { assertSocialWriteAllowed } from '../utils/verificationGate';

const isStudiZoneLayerData = (data: any) =>
  data?.layer === 'studizone' || data?.chatType === 'studizone_plan' || data?.isStudentOnly === true;

// Deterministic identity for a Vibe relationship — the authoritative
// `vibeRelationships/{pairKey}` doc lives at this id, independent of any
// individual `privateChats` document's (now freshly-generated, never
// reused) id. See useVibes.ts / unvibeUser (Cloud Function) for why the
// chat id itself must NOT be derived this way anymore.
const NOTIFICATION_PREFERENCE_FIELDS = [
  'notificationsEnabled', 'chatNotificationsEnabled', 'eventNotificationsEnabled',
  'eventRecommendationNotificationsEnabled', 'partnerNotificationsEnabled',
  'profileReminderNotificationsEnabled', 'reviewReminderNotificationsEnabled', 'feedbackReminderNotificationsEnabled',
] as const;

export function getVibePairKey(uidA: string, uidB: string): string {
  return [uidA, uidB].sort().join('_');
}

export function getChatCollectionName(kind: ChatItem['kind']): string {
  return kind === 'community'
    ? FIRESTORE_COLLECTIONS.partnerCommunityChats
    : kind === 'event'
    ? FIRESTORE_COLLECTIONS.eventChats
    : kind === 'plan'
    ? FIRESTORE_COLLECTIONS.planChats
    : kind === 'vibe'
    ? FIRESTORE_COLLECTIONS.privateChats
    : FIRESTORE_COLLECTIONS.matches;
}

type ChatReadState = Record<string, { lastReadAt: any }>;
const DEBUG_PROFILE_SUBSCRIPTIONS = false;

const firstString = (...values: unknown[]) => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim().length) {
      return value.trim();
    }
  }
  return '';
};

const toDateString = (value: any): string => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (typeof value.toDate === 'function') return value.toDate().toISOString();
  if (typeof value.seconds === 'number') return new Date(value.seconds * 1000).toISOString();
  return '';
};

const normalizeStudentVerificationStatus = (value: unknown): TonyteUser['studentVerificationStatus'] =>
  value === 'verified' ||
  value === 'pending' ||
  value === 'email_sent' ||
  value === 'failed' ||
  value === 'email_verification_sent' ||
  value === 'email_ownership_confirmed' ||
  value === 'pending_admin_review' ||
  value === 'approved' ||
  value === 'denied' ||
  value === 'rejected' ||
  value === 'expired'
    ? value
    : 'unverified';

const hasVerifiedStudentSignal = (data: any = {}) => {
  const status = normalizeStudentVerificationStatus(data.studentVerificationStatus);
  return (status === 'verified' || status === 'approved') && (
    data.isStudentVerified === true ||
    data.studentVerified === true ||
    Boolean(firstString(data.studentVerifiedAt, data.approvedAt))
  );
};

const normalizeProfile = (uid: string, data: any = {}): TonyteUser => {
  const role = normalizeUserRole(data.role || (data.isAdmin ? 'admin' : 'member'));
  const rawCity = firstString(data.locationCity, data.city, data.selectedEventCity);
  const city = rawCity && canAccessCity(normalizeCityKey(rawCity), role) ? rawCity : 'Dresden';
  const country = firstString(data.locationCountry, data.country);
  const photo = firstString(
    data.profilePhotoURL,
    data.profilePhoto,
    data.photoURL,
    data.profileImage,
    data.avatar,
    data.photo,
  );
  const coverPhoto = firstString(data.coverPhoto, data.coverImage, data.coverPhotoURL, data.coverImageUrl);

  return {
    id: uid,
    name: firstString(data.username, data.displayName, data.name) || 'Tonyte User',
    email: firstString(data.email),
    photo: photo || DEFAULT_AVATAR,
    profilePhotoURL: firstString(data.profilePhotoURL),
    profileImage: firstString(data.profileImage),
    profileImagePath: firstString(data.profileImagePath),
    profilePhoto: firstString(data.profilePhoto),
    profilePhotoPath: firstString(data.profilePhotoPath),
    photoURL: firstString(data.photoURL),
    photoPath: firstString(data.profileImagePath, data.photoPath, data.profilePhotoPath),
    profileComplete: data.profileComplete === true,
    isProfileComplete: data.isProfileComplete === true,
    onboardingComplete: data.onboardingComplete === true,
    profileSetupCompleted: data.profileSetupCompleted === true,
    requiresProfileSetup: data.requiresProfileSetup === true,
    coverPhoto: coverPhoto || DEFAULT_COVER,
    coverPhotoURL: firstString(data.coverPhotoURL),
    coverPhotoPath: firstString(data.coverImagePath, data.coverPhotoPath),
    bio: publicBio(data.bio, data.about),
    city,
    locationCity: city,
    country,
    locationCountry: country,
    flag: firstString(data.flag) || '🌍',
    // Prefer a dynamic calculation off dateOfBirth so age never goes stale;
    // fall back to the legacy stored age for users who predate DOB capture.
    age: typeof data.dateOfBirth === 'string' && data.dateOfBirth
      ? calculateAge(data.dateOfBirth)
      : typeof data.age === 'number' ? data.age : 0,
    dateOfBirth: typeof data.dateOfBirth === 'string' ? data.dateOfBirth : undefined,
    ageGroup: data.ageGroup,
    interests: Array.isArray(data.interests)
      ? data.interests.filter((item: unknown): item is string => typeof item === 'string' && item.trim().length > 0)
      : [],
    customInterests: Array.isArray(data.customInterests) ? data.customInterests : [],
    hashtags: Array.isArray(data.hashtags) ? data.hashtags : [],
    locationLabel: firstString(data.locationLabel) || (city ? `Based in ${city}${country ? `, ${country}` : ''}` : ''),
    username: firstString(data.username),
    role,
    isHost: Boolean(data.isHost || data.role === 'organizer' || data.role === 'admin' || data.role === 'co-admin'),
    isAdmin: Boolean(data.isAdmin || data.role === 'admin'),
    restricted: Boolean(data.restricted),
    discoverVisible: data.discoverVisible !== false,
    createdAt: toDateString(data.createdAt),
    lastActiveAt: toDateString(data.lastActiveAt),
    selectedEventCity: canAccessCity(normalizeCityKey(firstString(data.selectedEventCityKey, data.selectedEventCity)), role)
      ? firstString(data.selectedEventCity)
      : 'Dresden',
    selectedEventCityKey: canAccessCity(normalizeCityKey(firstString(data.selectedEventCityKey, data.selectedEventCity)), role)
      ? firstString(data.selectedEventCityKey)
      : 'dresden',
    followersCount: typeof data.followersCount === 'number' ? data.followersCount : 0,
    followingCount: typeof data.followingCount === 'number' ? data.followingCount : 0,
    isStudent: data.isStudent === true || hasVerifiedStudentSignal(data),
    isStudentVerified: hasVerifiedStudentSignal(data),
    studentVerified: hasVerifiedStudentSignal(data),
    studentVerificationStatus: hasVerifiedStudentSignal(data)
      ? 'verified'
      : normalizeStudentVerificationStatus(data.studentVerificationStatus),
    studentEmail: firstString(data.studentEmail, data.requestedStudentEmail),
    studentEmailDomain: firstString(data.studentEmailDomain, data.requestedStudentDomain),
    studentEmailOwnershipConfirmed: data.studentEmailOwnershipConfirmed === true,
    studentUniversity: firstString(data.studentUniversity),
    studentVerifiedAt: toDateString(data.studentVerifiedAt),
    requestedStudentEmail: firstString(data.requestedStudentEmail),
    requestedStudentDomain: firstString(data.requestedStudentDomain),
    approvedBy: firstString(data.approvedBy),
    approvedAt: toDateString(data.approvedAt),
    termsAccepted: data.termsAccepted === true,
    termsAcceptedAt: firstString(data.termsAcceptedAt),
    termsVersion: firstString(data.termsVersion),
    socialVibe: firstString(data.socialVibe),
    musicTaste: Array.isArray(data.musicTaste)
      ? (data.musicTaste as unknown[]).filter((v): v is string => typeof v === 'string')
      : [],
    languages: Array.isArray(data.languages)
      ? (data.languages as unknown[]).filter((v): v is string => typeof v === 'string')
      : [],
    preferredLanguage: (['en', 'de', 'es', 'fr', 'ru'].includes(String(data.preferredLanguage))
      ? String(data.preferredLanguage)
      : 'en') as TonyteUser['preferredLanguage'],
    goingOutStyle: Array.isArray(data.goingOutStyle)
      ? (data.goingOutStyle as unknown[]).filter((v): v is string => typeof v === 'string')
      : [],
    profileCompletionPercent: typeof data.profileCompletionPercent === 'number'
      ? data.profileCompletionPercent
      : undefined,
    nextProfileStep: firstString(data.nextProfileStep),
    missingProfileFields: Array.isArray(data.missingProfileFields)
      ? (data.missingProfileFields as unknown[]).filter((v): v is string => typeof v === 'string')
      : [],
    faceVerified: data.faceVerified === true,
    faceVerifiedAt: toDateString(data.faceVerifiedAt),
    verificationBadge: data.verificationBadge === 'real_person' ? 'real_person' as const : undefined,
    isVibeVerified: data.isVibeVerified === true || data.faceVerified === true,
    timeZone: typeof data.timeZone === 'string' ? data.timeZone : undefined,
    // Notification preferences (users/{uid}; functions/notifications.js reads the
    // same fields). Without these the Settings/Profile switches always showed "on"
    // and snapped back after turning them off.
    ...Object.fromEntries(NOTIFICATION_PREFERENCE_FIELDS
      .filter(field => typeof data[field] === 'boolean')
      .map(field => [field, data[field]])),
    faceVerificationStatus: (['not_started', 'pending', 'approved', 'rejected'].includes(String(data.faceVerificationStatus))
      ? String(data.faceVerificationStatus)
      : data.isVibeVerified === true || data.faceVerified === true
        ? 'approved'
        : 'not_started') as TonyteUser['faceVerificationStatus'],
    faceVerificationSubmittedAt: toDateString(data.faceVerificationSubmittedAt),
    faceVerificationApprovedAt: toDateString(data.faceVerificationApprovedAt),
    faceVerificationRejectedAt: toDateString(data.faceVerificationRejectedAt),
    latestFaceVerificationRequestId: firstString(data.latestFaceVerificationRequestId),
  };
};

export function useProfile(uid: string | null) {
  const effectiveUid = uid || auth.currentUser?.uid || null;
  const [profile, setProfile] = useState<TonyteUser | null>(null);
  const [loading, setLoading] = useState(Boolean(effectiveUid));
  const [error, setError] = useState<Error | null>(null);
  // A one-shot getDoc(), deliberately bypassing the shared onSnapshot
  // registry above (subscribeShared caches the last error and would just
  // replay it to a fresh subscriber rather than actually retrying — onSnapshot
  // itself already auto-retries transient network errors on its own; a
  // manual retry is only ever reached for something more permanent, like a
  // rules/permission problem, and a plain getDoc is the simplest way to
  // attempt it again without touching that shared-subscription internals).
  const retry = useCallback(async () => {
    if (!effectiveUid) return;
    setLoading(true);
    try {
      const snapshot = await getDoc(doc(firestore, FIRESTORE_COLLECTIONS.users, effectiveUid));
      const data = snapshot.exists() ? snapshot.data() : {};
      setProfile(normalizeProfile(effectiveUid, data));
      setError(null);
    } catch (retryError) {
      setError(retryError instanceof Error ? retryError : new Error(String(retryError)));
    } finally {
      setLoading(false);
    }
  }, [effectiveUid]);

  useEffect(() => {
    if (!effectiveUid) {
      setProfile(null);
      setLoading(false);
      setError(null);
      return undefined;
    }

    setLoading(true);
    setError(null);
    const path = `${FIRESTORE_COLLECTIONS.users}/${effectiveUid}`;

    // Shared: many screens mount useProfile(uid) for the same user at once
    // (nav root, feed, discover, plans, event detail, ...). Dedupe to a
    // single onSnapshot per uid instead of one per consumer. The self-heal
    // setDoc calls below run inside the shared `start` factory, so they fire
    // once per unique uid subscription rather than once per consumer.
    const unsubscribe = subscribeShared<TonyteUser | null>(
      `profile:${effectiveUid}`,
      (onData, onError) => {
        const ref = doc(firestore, FIRESTORE_COLLECTIONS.users, effectiveUid);
        if (DEBUG_PROFILE_SUBSCRIPTIONS && __DEV__) {
          console.log('[Tonyte][RN][Profile] starting user document subscription', {
            userId: effectiveUid,
            path,
          });
        }
        return onSnapshot(
          ref,
          snapshot => {
            if (DEBUG_PROFILE_SUBSCRIPTIONS && __DEV__) {
              console.log('[Tonyte][RN][Profile] user document snapshot received', {
                userId: effectiveUid,
                path,
                exists: snapshot.exists(),
                role: snapshot.data()?.role || null,
              });
            }
            const fallback = auth.currentUser
              ? {
                  name: auth.currentUser.displayName,
                  email: auth.currentUser.email,
                  photoURL: auth.currentUser.photoURL,
                }
              : {};
            const data = snapshot.exists() ? snapshot.data() : fallback;
            const dataRecord = data as Record<string, unknown>;
            const normalized = normalizeProfile(effectiveUid, data);
            if (snapshot.exists() && normalized.role !== 'admin') {
              const hiddenStoredCity = [
                dataRecord.city,
                dataRecord.locationCity,
                dataRecord.selectedEventCity,
                dataRecord.selectedEventCityKey,
              ].some(value => typeof value === 'string' && value.trim() && !canAccessCity(normalizeCityKey(value), normalized.role));
              if (hiddenStoredCity) {
                setDoc(ref, {
                  city: 'Dresden',
                  locationCity: 'Dresden',
                  selectedEventCity: 'Dresden',
                  selectedEventCityKey: 'dresden',
                  updatedAt: serverTimestamp(),
                }, { merge: true }).catch(error => {
                  console.warn('[Tonyte][RN][Profile] failed to migrate hidden city to Dresden', {
                    userId: effectiveUid,
                    error,
                  });
                });
              }
            }
            if (!snapshot.exists()) {
              console.warn('[Tonyte][RN][Profile] user document missing after auth bootstrap; retrying creation', {
                userId: effectiveUid,
                path,
              });
              setDoc(ref, {
                id: effectiveUid,
                email: auth.currentUser?.email || '',
                name: auth.currentUser?.displayName || '',
                photo: auth.currentUser?.photoURL || '',
                role: 'member',
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
              }, { merge: true }).then(() => {
                console.log('[Tonyte][RN][Profile] fallback user document creation succeeded', {
                  userId: effectiveUid,
                  path,
                  role: 'member',
                });
              }).catch(error => {
                console.warn('[Tonyte][RN][Profile] failed to create default user profile', {
                  userId: effectiveUid,
                  path,
                  role: 'member',
                  code: typeof error === 'object' && error !== null && 'code' in error
                    ? String((error as { code?: unknown }).code || '')
                    : '',
                  error,
                });
              });
            } else if (!snapshot.data()?.role) {
              setDoc(ref, { role: 'member', updatedAt: serverTimestamp() }, { merge: true }).catch(error => {
                console.warn('[Tonyte][RN][Profile] failed to set missing default role', {
                  userId: effectiveUid,
                  role: 'member',
                  error,
                });
              });
            }
            onData(normalized);
          },
          error => {
            console.error('[Tonyte][RN][Profile] user document subscription failed', {
              userId: effectiveUid,
              path,
              code: typeof error === 'object' && error !== null && 'code' in error
                ? String((error as { code?: unknown }).code || '')
                : '',
              message: error.message,
              error,
            });
            onError(error);
          },
        );
      },
      normalized => {
        setProfile(normalized);
        setLoading(false);
        setError(null);
      },
      subscriptionError => {
        setProfile(null);
        setLoading(false);
        setError(subscriptionError);
      },
    );

    return unsubscribe;
  }, [effectiveUid]);

  const updateProfile = useCallback(
    async (updates: Partial<TonyteUser>) => {
      if (!effectiveUid) return;
      const ref = doc(firestore, FIRESTORE_COLLECTIONS.users, effectiveUid);
      const patch = {
        ...updates,
        updatedAt: serverTimestamp(),
      };
      await setDoc(ref, patch, { merge: true });
      invalidateUserProfile(effectiveUid);

      if (auth.currentUser && (updates.name || updates.photo)) {
        await updateAuthProfile(auth.currentUser, {
          displayName: updates.name || auth.currentUser.displayName,
          photoURL: updates.photo || auth.currentUser.photoURL,
        });
      }
    },
    [effectiveUid],
  );

  return { profile, loading, error, retry, updateProfile };
}

export function usePublicProfile(userId: string | null) {
  const { profile, loading } = useProfile(userId);
  return { profile, loading };
}

const toPlanDateString = (value: unknown): string => {
  if (!value) return '';
  if (typeof value === 'string') return value.slice(0, 10);
  const v = value as any;
  if (typeof v.toDate === 'function') return v.toDate().toISOString().slice(0, 10);
  if (typeof v.seconds === 'number') return new Date(v.seconds * 1000).toISOString().slice(0, 10);
  return '';
};

export function usePlans(_uid: string | null) {
  const [plans, setPlans] = useState<TonyteActivity[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const planQueries = [
      query(
        collection(firestore, FIRESTORE_COLLECTIONS.plans),
        where('visibility', '==', 'public'),
        limit(50),
      ),
    ];
    const todayStr = new Date().toISOString().slice(0, 10); // 'YYYY-MM-DD'
    const snapshotsByIndex = new Map<number, TonyteActivity[]>();

    const normalizePlan = (id: string, data: any, sourceCollection: string): TonyteActivity => ({
      id,
      title: String(data.title || 'Untitled'),
      description: String(data.description || ''),
      hostId: String(data.hostId || data.createdBy || ''),
      hostName: String(data.hostName || 'Traveler'),
      startDate: toPlanDateString(data.startDate || data.date || data.createdAt),
      endDate: toPlanDateString(data.endDate) || undefined,
      startTime: String(data.startTime || ''),
      endTime: data.endTime ? String(data.endTime) : undefined,
      endAt: data.endAt,
      city: String(data.city || ''),
      cityKey: String(data.cityKey || data.city || ''),
      entryType: 'community_plan' as const,
      type: 'plan',
      collection: sourceCollection,
      sourceCollection,
      category: 'social' as const,
      sourceType: 'user_activity' as const,
      approved: true,
      isFree: data.isFree !== false,
      maxParticipants: Number(data.maxParticipants || 0),
      interestedCount: Number(data.participants || data.interestedCount || 0),
      coverPhoto: data.coverPhoto,
      coverImageUrl: data.coverImageUrl || data.coverPhoto || data.imageUrl || data.image,
      imageUrl: data.imageUrl,
      image: data.image,
      photoUrl: data.photoUrl,
      location: String(data.location || data.city || ''),
      address: String(data.address || ''),
      locationName: String(data.locationName || data.venueName || ''),
      coordinates: null,
      createdAt: toDateString(data.createdAt) || new Date().toISOString(),
      createdBy: String(data.createdBy || data.hostId || ''),
      visibility: data.visibility,
      tags: Array.isArray(data.tags) ? data.tags : [],
      hashtags: Array.isArray(data.hashtags) ? data.hashtags : [],
      attendees: Array.isArray(data.attendees) ? data.attendees : [],
      participants: typeof data.participants === 'number' ? data.participants : 0,
      participantIds: Array.isArray(data.participantIds) ? data.participantIds : [],
      adminIds: Array.isArray(data.adminIds) ? data.adminIds : [],
      removedUserIds: Array.isArray(data.removedUserIds) ? data.removedUserIds : [],
    } as TonyteActivity);

    const publishPlans = () => {
      const byId = new Map<string, TonyteActivity>();
      Array.from(snapshotsByIndex.values()).flat().forEach(plan => {
        if (isPlanActivity(plan)) byId.set(plan.id, plan);
      });
      const items = Array.from(byId.values());
      items.sort((a, b) => (b.createdAt > a.createdAt ? 1 : -1));
      setPlans(items);
      setLoading(false);
    };

    const unsubs = planQueries.map((planQuery, index) => onSnapshot(
      planQuery,
      snapshot => {
        const items: TonyteActivity[] = snapshot.docs
          .filter(d => {
            const data = d.data();
            if (isStudiZoneLayerData(data)) return false;
            // "Plan ends at" (endAt): gone from the list the moment it ends.
            const endsAt = data.endAt && typeof data.endAt.toMillis === 'function' ? data.endAt.toMillis() : null;
            if (endsAt !== null) return endsAt >= Date.now();
            // Only filter when there is an explicit date field — plans with no date are undated/ongoing
            const rawDate = data.endDate || data.startDate || data.date;
            if (!rawDate) return true;
            const dateStr = toPlanDateString(rawDate);
            // Keep if today or in the future (empty dateStr = keep)
            return !dateStr || dateStr >= todayStr;
          })
          .map(d => normalizePlan(d.id, d.data(), d.ref.parent.id))
          .filter(isPlanActivity);
        snapshotsByIndex.set(index, items);
        publishPlans();
      },
      () => setLoading(false),
    ));
    return () => {
      unsubs.forEach(unsub => unsub());
    };
  }, []);

  return { plans, loading };
}

const formatChatTime = (value: any, locale: string) => {
  if (!value) return '';
  const date = typeof value === 'string'
    ? new Date(value)
    : typeof value.toDate === 'function'
      ? value.toDate()
      : typeof value.seconds === 'number'
        ? new Date(value.seconds * 1000)
        : null;
  if (!date || Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
};

const getUnreadCount = (data: any, uid: string): number => {
  const val = data?.unreadCountByUser?.[uid];
  return typeof val === 'number' ? val : 0;
};

const getUnreadFlag = (data: any, uid: string): boolean => (
  getUnreadCount(data, uid) > 0 ||
  data?.unreadByUser?.[uid] === true ||
  data?.hasUnreadByUser?.[uid] === true ||
  data?.unread === true
);

const isDeletedForUser = (data: any, uid: string): boolean => data?.deletedFor?.[uid] === true;

const getChatTimeMs = (value: any): number => {
  if (!value) return 0;
  if (typeof value === 'string') return new Date(value).getTime();
  if (typeof value.toDate === 'function') return value.toDate().getTime();
  if (typeof value.seconds === 'number') return value.seconds * 1000;
  return 0;
};

const getTimestampMs = (value: any): number => {
  if (!value) return 0;
  if (typeof value === 'string') {
    const ms = new Date(value).getTime();
    return Number.isNaN(ms) ? 0 : ms;
  }
  if (typeof value.toDate === 'function') return value.toDate().getTime();
  if (typeof value.seconds === 'number') return value.seconds * 1000;
  return 0;
};

const getLastActivityValue = (data: any) =>
  data?.lastActivityAt ?? data?.lastMessageAt ?? data?.updatedAt ?? data?.createdAt;

const getChatUnreadState = (data: any, uid: string, chatId: string, readByChat: ChatReadState) => {
  const legacyCount = getUnreadCount(data, uid);
  const lastMessageAtMs = getTimestampMs(data.lastMessageAt);
  const lastReadAtMs = getTimestampMs(readByChat[chatId]?.lastReadAt ?? data?.lastReadAt?.[uid]);
  const lastSenderId = firstString(data.lastMessageSenderId);
  const isReadByTimestamp = lastMessageAtMs > 0 && lastReadAtMs >= lastMessageAtMs;
  const timestampUnread = Boolean(
    lastSenderId &&
    lastSenderId !== uid &&
    lastMessageAtMs > 0 &&
    lastMessageAtMs > lastReadAtMs
  );
  const legacyUnread = !isReadByTimestamp && (
    legacyCount > 0 || (lastSenderId !== uid && getUnreadFlag(data, uid))
  );
  const hasUnread = timestampUnread || legacyUnread;

  return {
    unread: hasUnread,
    unreadCount: hasUnread ? Math.max(legacyCount, 1) : 0,
  };
};

// Shared across useChats/useChatUnreadSummary/useStudiZoneChats/useChatNotifications
// so a user with multiple of these hooks mounted at once (e.g. nav-root badge +
// chat list screen) gets one chatReads listener instead of N.
function useChatReads(uid: string | null) {
  const { docs, loaded } = useSharedQueryDocs(
    uid ? `chatReads:${uid}` : null,
    () => collection(firestore, FIRESTORE_COLLECTIONS.users, uid as string, 'chatReads'),
  );

  const readByChat = useMemo(() => {
    const next: ChatReadState = {};
    docs.forEach(readDoc => {
      next[readDoc.id] = { lastReadAt: readDoc.data().lastReadAt };
    });
    return next;
  }, [docs]);

  return { readByChat, loaded };
}

// Shared: previously duplicated verbatim in useChatNotifications.ts, each
// opening its own onSnapshot on the same users/{uid}/mutedChats collection.
export function useMutedChatIds(uid: string | null): Set<string> {
  const { docs } = useSharedQueryDocs(
    uid ? `mutedChats:${uid}` : null,
    () => collection(firestore, FIRESTORE_COLLECTIONS.users, uid as string, 'mutedChats'),
  );

  return useMemo(() => new Set(docs.map(d => d.id)), [docs]);
}

export function useSingleChatMuted(uid: string | null, chatId: string | null): boolean {
  const [isMuted, setIsMuted] = useState(false);

  useEffect(() => {
    if (!uid || !chatId) {
      setIsMuted(false);
      return undefined;
    }
    const unsub = onSnapshot(
      doc(firestore, FIRESTORE_COLLECTIONS.users, uid, 'mutedChats', chatId),
      snap => setIsMuted(snap.exists()),
      () => setIsMuted(false),
    );
    return unsub;
  }, [uid, chatId]);

  return isMuted;
}

export async function muteChatForUser(userId: string, chatId: string): Promise<void> {
  await setDoc(
    doc(firestore, FIRESTORE_COLLECTIONS.users, userId, 'mutedChats', chatId),
    { chatId, mutedAt: serverTimestamp(), source: 'chat' },
  );
}

export async function unmuteChatForUser(userId: string, chatId: string): Promise<void> {
  await deleteDoc(doc(firestore, FIRESTORE_COLLECTIONS.users, userId, 'mutedChats', chatId));
}

export function useChats(uid: string | null) {
  const { language, t } = useI18n();
  const locale = LANGUAGE_LOCALES[language];
  const { readByChat, loaded: readsLoaded } = useChatReads(uid);
  const mutedChatIds = useMutedChatIds(uid);

  // Shared raw-doc subscriptions: identical queries are also used by
  // useChatUnreadSummary / useChatNotifications / useStudiZoneChats, so
  // these listeners are opened once per uid and reused across all of them
  // instead of once per hook instance.
  const { docs: blockedDocs, loaded: blocksLoaded } = useSharedQueryDocs(
    uid ? `blockedUsers:${uid}` : null,
    () => collection(firestore, FIRESTORE_COLLECTIONS.users, uid as string, 'blockedUsers'),
  );
  const { docs: compatBlockDocs, loaded: compatBlocksLoaded } = useSharedQueryDocs(
    uid ? `compatBlocks:${uid}` : null,
    () => query(collection(firestore, FIRESTORE_COLLECTIONS.blocks), where('blockerId', '==', uid as string)),
  );
  const { docs: eventChatDocs, loaded: eventLoaded } = useSharedQueryDocs(
    uid ? `eventChats:${uid}` : null,
    () => query(collection(firestore, FIRESTORE_COLLECTIONS.eventChats), where('memberIds', 'array-contains', uid as string)),
  );
  const { docs: planChatDocs, loaded: planLoaded } = useSharedQueryDocs(
    uid ? `planChats:${uid}` : null,
    () => query(collection(firestore, FIRESTORE_COLLECTIONS.planChats), where('memberIds', 'array-contains', uid as string)),
  );
  const { docs: matchDocs, loaded: matchDocsLoaded } = useSharedQueryDocs(
    uid ? `matches:${uid}` : null,
    () => query(collection(firestore, FIRESTORE_COLLECTIONS.matches), where('users', 'array-contains', uid as string)),
  );
  const { docs: vibeDocs, loaded: vibeDocsLoaded } = useSharedQueryDocs(
    uid ? `privateChats:${uid}` : null,
    () => query(collection(firestore, FIRESTORE_COLLECTIONS.privateChats), where('users', 'array-contains', uid as string)),
  );

  const blockedUserIds = useMemo(() => blockedDocs.map(blockDoc => blockDoc.id), [blockedDocs]);
  const compatBlockedUserIds = useMemo(
    () => compatBlockDocs.map(blockDoc => firstString(blockDoc.data().blockedId)).filter(Boolean),
    [compatBlockDocs],
  );

  const eventChats = useMemo<ChatItem[]>(() => {
    if (!uid) return [];
    return eventChatDocs
      .filter(chatDoc => !isDeletedForUser(chatDoc.data(), uid) && !isStudiZoneLayerData(chatDoc.data()))
      .map(chatDoc => {
        const data = chatDoc.data();
        const unreadState = getChatUnreadState(data, uid, chatDoc.id, readByChat);
        return {
          id: chatDoc.id,
          name: firstString(data.eventTitle, data.name) || t('chat.eventChat'),
          lastMsg: firstString(data.lastMessage),
          time: formatChatTime(getLastActivityValue(data), locale),
          lastActivityAtMs: getTimestampMs(getLastActivityValue(data)),
          photo: firstString(data.photo, data.eventImage) || DEFAULT_COVER,
          unread: unreadState.unread,
          unreadCount: unreadState.unreadCount,
          lastMessageSenderId: firstString(data.lastMessageSenderId),
          lastReadAt: data.lastReadAt ?? {},
          participantIds: Array.isArray(data.memberIds) ? data.memberIds : [],
          kind: 'event' as const,
          eventId: firstString(data.eventId) || chatDoc.id,
          eventTitle: firstString(data.eventTitle),
          isMuted: mutedChatIds.has(chatDoc.id),
        };
      });
  }, [eventChatDocs, locale, readByChat, mutedChatIds, t, uid]);

  const planChats = useMemo<ChatItem[]>(() => {
    if (!uid) return [];
    return planChatDocs
      .filter(chatDoc => !isDeletedForUser(chatDoc.data(), uid) && !isStudiZoneLayerData(chatDoc.data()))
      .map(chatDoc => {
        const data = chatDoc.data();
        const unreadState = getChatUnreadState(data, uid, chatDoc.id, readByChat);
        return {
          id: chatDoc.id,
          name: firstString(data.planTitle, data.name) || t('chat.planChat'),
          lastMsg: firstString(data.lastMessage),
          time: formatChatTime(getLastActivityValue(data), locale),
          lastActivityAtMs: getTimestampMs(getLastActivityValue(data)),
          photo: firstString(data.photo, data.planImage) || DEFAULT_COVER,
          unread: unreadState.unread,
          unreadCount: unreadState.unreadCount,
          lastMessageSenderId: firstString(data.lastMessageSenderId),
          lastReadAt: data.lastReadAt ?? {},
          participantIds: Array.isArray(data.memberIds) ? data.memberIds : [],
          kind: 'plan' as const,
          planId: firstString(data.planId) || chatDoc.id,
          planTitle: firstString(data.planTitle),
          isMuted: mutedChatIds.has(chatDoc.id),
        };
      });
  }, [locale, planChatDocs, readByChat, mutedChatIds, t, uid]);

  // match/vibe chats need an async per-doc profile lookup (getUserProfile),
  // so they stay effect-driven rather than a pure useMemo.
  const [matchChats, setMatchChats] = useState<ChatItem[]>([]);
  const [vibeChats, setVibeChats] = useState<ChatItem[]>([]);
  const [matchReady, setMatchReady] = useState(false);
  const [vibeReady, setVibeReady] = useState(false);

  useEffect(() => {
    setMatchReady(false);
    setVibeReady(false);
  }, [uid]);

  useEffect(() => {
    if (!uid) {
      setMatchChats([]);
      return undefined;
    }
    let active = true;
    Promise.all(matchDocs.map(async chatDoc => {
      const data = chatDoc.data();
      if (isDeletedForUser(data, uid)) return null;
      const unreadState = getChatUnreadState(data, uid, chatDoc.id, readByChat);
      const participants: string[] = Array.isArray(data.participants)
        ? data.participants
        : Array.isArray(data.users)
        ? data.users
        : [];
      const otherUserId = participants.find(participantId => participantId !== uid) || '';
      const otherProfile = await getUserProfile(otherUserId);
      return {
        id: chatDoc.id,
        name: getUserDisplayName(otherProfile),
        lastMsg: firstString(data.lastMessage),
        time: formatChatTime(getLastActivityValue(data), locale),
        lastActivityAtMs: getTimestampMs(getLastActivityValue(data)),
        photo: otherProfile.profileImage,
        otherUserVibeVerified: otherProfile.faceVerified === true,
        unread: unreadState.unread,
        unreadCount: unreadState.unreadCount,
        lastMessageSenderId: firstString(data.lastMessageSenderId),
        lastReadAt: data.lastReadAt ?? {},
        participantIds: participants,
        kind: 'match' as const,
        isMuted: mutedChatIds.has(chatDoc.id),
      };
    })).then(items => {
      if (!active) return;
      setMatchChats(items.filter(Boolean) as ChatItem[]);
      setMatchReady(true);
    });
    return () => {
      active = false;
    };
  }, [locale, matchDocs, readByChat, mutedChatIds, uid]);

  useEffect(() => {
    if (!uid) {
      setVibeChats([]);
      return undefined;
    }
    let active = true;
    Promise.all(vibeDocs.map(async chatDoc => {
      const data = chatDoc.data();
      if (isDeletedForUser(data, uid)) return null;
      const unreadState = getChatUnreadState(data, uid, chatDoc.id, readByChat);
      const participants: string[] = Array.isArray(data.users) ? data.users : [];
      const otherUserId = participants.find(id => id !== uid) || '';
      const otherProfile = await getUserProfile(otherUserId);
      return {
        id: chatDoc.id,
        name: getUserDisplayName(otherProfile),
        lastMsg: firstString(data.lastMessage),
        time: formatChatTime(getLastActivityValue(data), locale),
        lastActivityAtMs: getTimestampMs(getLastActivityValue(data)),
        photo: otherProfile.profileImage,
        otherUserVibeVerified: otherProfile.faceVerified === true,
        unread: unreadState.unread,
        unreadCount: unreadState.unreadCount,
        lastMessageSenderId: firstString(data.lastMessageSenderId),
        lastReadAt: data.lastReadAt ?? {},
        participantIds: participants,
        kind: 'vibe' as const,
        isMuted: mutedChatIds.has(chatDoc.id),
      };
    })).then(items => {
      if (!active) return;
      setVibeChats(items.filter(Boolean) as ChatItem[]);
      setVibeReady(true);
    });
    return () => {
      active = false;
    };
  }, [locale, vibeDocs, readByChat, mutedChatIds, uid]);

  const loading = Boolean(uid) && !(blocksLoaded && compatBlocksLoaded && eventLoaded && planLoaded && matchDocsLoaded && matchReady && vibeDocsLoaded && vibeReady);

  return useMemo(() => {
    const blocked = new Set([...blockedUserIds, ...compatBlockedUserIds]);
    const visibleMatchChats = matchChats.filter(chat => (
      !chat.participantIds.some(participantId => participantId !== uid && blocked.has(participantId))
    ));
    const visibleVibeChats = vibeChats.filter(chat => (
      !chat.participantIds.some(participantId => participantId !== uid && blocked.has(participantId))
    ));
    const all = [...eventChats, ...planChats, ...visibleMatchChats, ...visibleVibeChats].sort(
      (a, b) => b.lastActivityAtMs - a.lastActivityAtMs,
    );
    return { chats: all, loading: loading || !readsLoaded };
  }, [blockedUserIds, compatBlockedUserIds, eventChats, loading, matchChats, planChats, vibeChats, readsLoaded, uid]);
}

export function useStudiZoneChats(uid: string | null) {
  const { language, t } = useI18n();
  const locale = LANGUAGE_LOCALES[language];
  const { readByChat, loaded: readsLoaded } = useChatReads(uid);
  const mutedChatIds = useMutedChatIds(uid);
  // Same shared key as useChats' planChats subscription — one listener
  // serves both the regular and StudiZone chat lists.
  const { docs: planChatDocs, loaded: planLoaded } = useSharedQueryDocs(
    uid ? `planChats:${uid}` : null,
    () => query(collection(firestore, FIRESTORE_COLLECTIONS.planChats), where('memberIds', 'array-contains', uid as string)),
  );

  const chats = useMemo<ChatItem[]>(() => {
    if (!uid) return [];
    return planChatDocs
      .filter(chatDoc => !isDeletedForUser(chatDoc.data(), uid) && isStudiZoneLayerData(chatDoc.data()))
      .map(chatDoc => {
        const data = chatDoc.data();
        const unreadState = getChatUnreadState(data, uid, chatDoc.id, readByChat);
        return {
          id: chatDoc.id,
          name: firstString(data.planTitle, data.name) || t('chat.studiZonePlan'),
          lastMsg: firstString(data.lastMessage),
          time: formatChatTime(getLastActivityValue(data), locale),
          lastActivityAtMs: getTimestampMs(getLastActivityValue(data)),
          photo: firstString(data.photo, data.planImage) || DEFAULT_COVER,
          unread: unreadState.unread,
          unreadCount: unreadState.unreadCount,
          lastMessageSenderId: firstString(data.lastMessageSenderId),
          lastReadAt: data.lastReadAt ?? {},
          participantIds: Array.isArray(data.memberIds) ? data.memberIds : Array.isArray(data.members) ? data.members : [],
          kind: 'plan' as const,
          planId: firstString(data.planId) || chatDoc.id,
          planTitle: firstString(data.planTitle),
          layer: data.layer,
          chatType: data.chatType,
          isStudentOnly: data.isStudentOnly,
          isMuted: mutedChatIds.has(chatDoc.id),
        };
      })
      .sort((a, b) => b.lastActivityAtMs - a.lastActivityAtMs);
  }, [locale, planChatDocs, readByChat, mutedChatIds, t, uid]);

  return { chats, loading: (Boolean(uid) && !planLoaded) || !readsLoaded };
}

export function useChatUnreadSummary(uid: string | null) {
  const mutedChatIds = useMutedChatIds(uid);
  const { readByChat, loaded: readsLoaded } = useChatReads(uid);

  // Same shared keys as useChats: when both are mounted (nav-root badge +
  // chat list screen) these reuse a single onSnapshot per collection.
  const { docs: eventChatDocs, loaded: eventLoaded } = useSharedQueryDocs(
    uid ? `eventChats:${uid}` : null,
    () => query(collection(firestore, FIRESTORE_COLLECTIONS.eventChats), where('memberIds', 'array-contains', uid as string)),
  );
  const { docs: planChatDocs, loaded: planLoaded } = useSharedQueryDocs(
    uid ? `planChats:${uid}` : null,
    () => query(collection(firestore, FIRESTORE_COLLECTIONS.planChats), where('memberIds', 'array-contains', uid as string)),
  );
  const { docs: matchDocs, loaded: matchLoaded } = useSharedQueryDocs(
    uid ? `matches:${uid}` : null,
    () => query(collection(firestore, FIRESTORE_COLLECTIONS.matches), where('users', 'array-contains', uid as string)),
  );
  const { docs: vibeDocs, loaded: vibeLoaded } = useSharedQueryDocs(
    uid ? `privateChats:${uid}` : null,
    () => query(collection(firestore, FIRESTORE_COLLECTIONS.privateChats), where('users', 'array-contains', uid as string)),
  );
  const { docs: blockedDocs, loaded: blocksLoaded } = useSharedQueryDocs(
    uid ? `blockedUsers:${uid}` : null,
    () => collection(firestore, FIRESTORE_COLLECTIONS.users, uid as string, 'blockedUsers'),
  );
  const { docs: compatBlockDocs, loaded: compatBlocksLoaded } = useSharedQueryDocs(
    uid ? `compatBlocks:${uid}` : null,
    () => query(collection(firestore, FIRESTORE_COLLECTIONS.blocks), where('blockerId', '==', uid as string)),
  );

  const blockedUserIds = useMemo(() => blockedDocs.map(blockDoc => blockDoc.id), [blockedDocs]);
  const compatBlockedUserIds = useMemo(
    () => compatBlockDocs.map(blockDoc => firstString(blockDoc.data().blockedId)).filter(Boolean),
    [compatBlockDocs],
  );

  const eventUnreadByChat = useMemo(() => {
    if (!uid) return {} as Record<string, { count: number; hasUnread: boolean }>;
    const next: Record<string, { count: number; hasUnread: boolean }> = {};
    eventChatDocs.filter(chatDoc => !isDeletedForUser(chatDoc.data(), uid)).forEach(chatDoc => {
      const state = getChatUnreadState(chatDoc.data(), uid, chatDoc.id, readByChat);
      next[chatDoc.id] = { count: state.unreadCount, hasUnread: state.unread };
    });
    return next;
  }, [eventChatDocs, readByChat, uid]);

  const planUnreadByChat = useMemo(() => {
    if (!uid) return {} as Record<string, { count: number; hasUnread: boolean }>;
    const next: Record<string, { count: number; hasUnread: boolean }> = {};
    planChatDocs.filter(chatDoc => !isDeletedForUser(chatDoc.data(), uid)).forEach(chatDoc => {
      const state = getChatUnreadState(chatDoc.data(), uid, chatDoc.id, readByChat);
      next[chatDoc.id] = { count: state.unreadCount, hasUnread: state.unread };
    });
    return next;
  }, [planChatDocs, readByChat, uid]);

  const matchUnreadByChat = useMemo(() => {
    if (!uid) return {} as Record<string, { count: number; hasUnread: boolean; otherUserId: string }>;
    const next: Record<string, { count: number; hasUnread: boolean; otherUserId: string }> = {};
    matchDocs.forEach(chatDoc => {
      const data = chatDoc.data();
      if (isDeletedForUser(data, uid)) return;
      const participants: string[] = Array.isArray(data.participants)
        ? data.participants
        : Array.isArray(data.users)
        ? data.users
        : [];
      const otherUserId = participants.find(participantId => participantId !== uid) || '';
      const unreadState = getChatUnreadState(data, uid, chatDoc.id, readByChat);
      next[chatDoc.id] = { count: unreadState.unreadCount, hasUnread: unreadState.unread, otherUserId };
    });
    return next;
  }, [matchDocs, readByChat, uid]);

  const vibeUnreadByChat = useMemo(() => {
    if (!uid) return {} as Record<string, { count: number; hasUnread: boolean; otherUserId: string }>;
    const next: Record<string, { count: number; hasUnread: boolean; otherUserId: string }> = {};
    vibeDocs.forEach(chatDoc => {
      const data = chatDoc.data();
      if (isDeletedForUser(data, uid)) return;
      const participants: string[] = Array.isArray(data.users) ? data.users : [];
      const otherUserId = participants.find(id => id !== uid) || '';
      const unreadState = getChatUnreadState(data, uid, chatDoc.id, readByChat);
      next[chatDoc.id] = { count: unreadState.unreadCount, hasUnread: unreadState.unread, otherUserId };
    });
    return next;
  }, [vibeDocs, readByChat, uid]);

  const hasLoaded = !uid || (eventLoaded && planLoaded && matchLoaded && vibeLoaded && blocksLoaded && compatBlocksLoaded);

  return useMemo(() => {
    const blocked = new Set([...blockedUserIds, ...compatBlockedUserIds]);

    const eventUnreadCount = Object.entries(eventUnreadByChat)
      .filter(([id]) => !mutedChatIds.has(id))
      .reduce((total, [, item]) => total + item.count, 0);
    const eventHasUnread = Object.entries(eventUnreadByChat)
      .some(([id, item]) => !mutedChatIds.has(id) && item.hasUnread);

    const planUnreadCount = Object.entries(planUnreadByChat)
      .filter(([id]) => !mutedChatIds.has(id))
      .reduce((total, [, item]) => total + item.count, 0);
    const planHasUnread = Object.entries(planUnreadByChat)
      .some(([id, item]) => !mutedChatIds.has(id) && item.hasUnread);

    const matchUnreadCount = Object.entries(matchUnreadByChat).reduce((total, [id, item]) => (
      (item.otherUserId && blocked.has(item.otherUserId)) || mutedChatIds.has(id) ? total : total + item.count
    ), 0);
    const matchHasUnread = Object.entries(matchUnreadByChat).some(([id, item]) => (
      !(item.otherUserId && blocked.has(item.otherUserId)) && !mutedChatIds.has(id) && item.hasUnread
    ));

    const vibeUnreadCount = Object.entries(vibeUnreadByChat).reduce((total, [id, item]) => (
      (item.otherUserId && blocked.has(item.otherUserId)) || mutedChatIds.has(id) ? total : total + item.count
    ), 0);
    const vibeHasUnread = Object.entries(vibeUnreadByChat).some(([id, item]) => (
      !(item.otherUserId && blocked.has(item.otherUserId)) && !mutedChatIds.has(id) && item.hasUnread
    ));

    const unreadChatCount = eventUnreadCount + planUnreadCount + matchUnreadCount + vibeUnreadCount;
    return {
      hasUnreadMessages: unreadChatCount > 0 || eventHasUnread || planHasUnread || matchHasUnread || vibeHasUnread,
      unreadChatCount,
      loading: !hasLoaded || !readsLoaded,
    };
  }, [blockedUserIds, compatBlockedUserIds, eventUnreadByChat, hasLoaded, matchUnreadByChat, mutedChatIds, planUnreadByChat, vibeUnreadByChat, readsLoaded]);
}

function mapChatMessageDoc(d: QueryDocumentSnapshot<DocumentData>, chatId: string): ChatMessage {
    const raw = d.data();
    const se = raw.sharedEvent && typeof raw.sharedEvent === 'object' ? raw.sharedEvent : null;
    const sv = raw.sharedVenue && typeof raw.sharedVenue === 'object' ? raw.sharedVenue : null;
    const gif = raw.gif && typeof raw.gif === 'object' ? raw.gif : null;
    const rt = raw.replyTo && typeof raw.replyTo === 'object' ? raw.replyTo : null;
    const type = raw.type === 'shared_event'
      ? 'shared_event' as const
      : raw.type === 'shared_venue'
        ? 'shared_venue' as const
        : raw.type === 'image'
          ? 'image' as const
          : raw.type === 'gif'
            ? 'gif' as const
            : raw.type === 'voice'
              ? 'voice' as const
              : raw.type === 'vibe_icebreaker'
                ? 'vibe_icebreaker' as const
                : raw.type === 'member_event'
                  ? 'member_event' as const
                  : 'text' as const;
    return {
      id: d.id,
      chatId,
      type,
      eventId: typeof raw.eventId === 'string' ? raw.eventId.trim() : undefined,
      text: String(raw.text ?? ''),
      senderId: String(raw.senderId ?? ''),
      senderName: String(raw.senderName ?? 'Traveler'),
      senderPhotoURL: raw.senderPhotoURL,
      memberEvent: raw.memberEvent === 'joined' || raw.memberEvent === 'left' ? raw.memberEvent : undefined,
      prompts: Array.isArray(raw.prompts) ? raw.prompts.map(String).filter(Boolean) : undefined,
      date: typeof raw.date === 'string' ? raw.date : undefined,
      createdAt: raw.createdAt,
      imageUrl: typeof raw.imageUrl === 'string' && raw.imageUrl ? raw.imageUrl : undefined,
      gif: gif
        ? {
            id: typeof gif.id === 'string' ? gif.id : '',
            title: typeof gif.title === 'string' ? gif.title : 'GIF',
            url: typeof gif.url === 'string' ? gif.url : '',
            previewUrl: typeof gif.previewUrl === 'string' ? gif.previewUrl : '',
            width: typeof gif.width === 'number' ? gif.width : 320,
            height: typeof gif.height === 'number' ? gif.height : 240,
            provider: typeof gif.provider === 'string' ? gif.provider : 'giphy',
          }
        : undefined,
      audioUrl: typeof raw.audioUrl === 'string' && raw.audioUrl ? raw.audioUrl : undefined,
      audioStoragePath: typeof raw.audioStoragePath === 'string' && raw.audioStoragePath ? raw.audioStoragePath : undefined,
      duration: typeof raw.duration === 'number' ? raw.duration : undefined,
      waveformData: Array.isArray(raw.waveformData)
        ? raw.waveformData.map(Number).filter(value => Number.isFinite(value))
        : undefined,
      status: raw.status === 'uploading' || raw.status === 'failed' || raw.status === 'sent' ? raw.status : undefined,
      sharedEvent: se
        ? {
            eventId: typeof se.eventId === 'string' ? se.eventId.trim() : '',
            title: typeof se.title === 'string' ? se.title.trim() : 'Tonyte Event',
            time: typeof se.time === 'string' ? se.time.trim() : '',
            location: typeof se.location === 'string' ? se.location.trim() : '',
            teaser: typeof se.teaser === 'string' ? se.teaser.trim() : '',
            imageUrl: typeof se.imageUrl === 'string' ? se.imageUrl.trim() : '',
            link: typeof se.link === 'string' ? se.link.trim() : '',
            endAt: typeof se.endAt === 'string' ? se.endAt : undefined,
          }
        : undefined,
      sharedVenue: sv
        ? {
            venueId: typeof sv.venueId === 'string' ? sv.venueId.trim() : '',
            name: typeof sv.name === 'string' ? sv.name.trim() : 'Tonyte Venue',
            category: typeof sv.category === 'string' ? sv.category.trim() : '',
            coverUrl: typeof sv.coverUrl === 'string' ? sv.coverUrl.trim() : '',
            city: typeof sv.city === 'string' ? sv.city.trim() : '',
            lat: typeof sv.lat === 'number' ? sv.lat : undefined,
            lng: typeof sv.lng === 'number' ? sv.lng : undefined,
            address: typeof sv.address === 'string' ? sv.address.trim() : undefined,
          }
        : undefined,
      replyTo: rt
        ? {
            messageId: typeof rt.messageId === 'string' ? rt.messageId : '',
            senderName: typeof rt.senderName === 'string' ? rt.senderName : '',
            text: typeof rt.text === 'string' ? rt.text : '',
          }
        : undefined,
    };
}

export function useChatMessages(chatId: string | null, kind: ChatItem['kind']) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(Boolean(chatId));

  useEffect(() => {
    if (!chatId) {
      setMessages([]);
      setLoading(false);
      return undefined;
    }

    const collectionName = getChatCollectionName(kind);

    setLoading(true);
    const messagesRef = collection(firestore, collectionName, chatId, 'messages');
    const q = query(messagesRef, orderBy('createdAt', 'asc'));
    const mappedById = new Map<string, ChatMessage>();
    // [ChatSendDebug] TEMPORARY — see sendChatMessage's matching note.
    if (__DEV__) console.log('[ChatSendDebug] message listener attached', {
      chatId,
      kind,
      collectionName,
      path: messagesRef.path,
    });

    const unsub = onSnapshot(
      q,
      snapshot => {
        if (__DEV__) console.log('[ChatSendDebug] message listener snapshot', {
          chatId,
          snapshotSize: snapshot.size,
          fromCache: snapshot.metadata.fromCache,
          hasPendingWrites: snapshot.metadata.hasPendingWrites,
        });
        // snapshot.docs already arrive ordered by the orderBy('createdAt', 'asc')
        // clause above, so no client-side re-sort is needed here (previously this
        // re-sorted the entire message history — O(n log n) — on every single
        // new message).
        // Reuse the mapped object for every message this snapshot didn't
        // touch, so memoized bubbles keep their props and skip re-rendering
        // when a single message arrives or changes.
        snapshot.docChanges().forEach(change => {
          if (change.type === 'removed') mappedById.delete(change.doc.id);
          else mappedById.set(change.doc.id, mapChatMessageDoc(change.doc, chatId));
        });
        setMessages(snapshot.docs.map(d => mappedById.get(d.id) ?? mapChatMessageDoc(d, chatId)));
        setLoading(false);
      },
      error => {
        console.warn('[Tonyte][RN][Messages] subscription failed', {
          path: `${collectionName}/${chatId}/messages`,
          action: 'onSnapshot',
          error,
        });
        setLoading(false);
      },
    );

    return unsub;
  }, [chatId, kind]);

  return { messages, loading };
}

// [ChatEnvDebug] TEMPORARY — emulator-vs-real-device differential audit.
// Logs everything needed to compare a working emulator session against a
// failing physical-device session: which Firebase project/app the build
// actually points at, which build variant is installed, which account is
// authenticated and whether its ID token/profile doc are actually reachable,
// and a real authoritative (server-source) Firestore read with timing — so a
// device that's silently stuck offline shows up as a slow/failed probe
// rather than a mysterious "message just didn't arrive". Fires once per
// chat open, not on every render. Remove once the device-only bug is closed.
let chatEnvDebugLoggedForChatId: string | null = null;

export async function logChatEnvDebug(chatId: string, kind: ChatItem['kind']): Promise<void> {
  // Dev-only: in release it cost a forced token refresh + 3 Firestore reads
  // on every chat open and logged the user's email to the device log.
  if (!__DEV__ || !chatId || chatEnvDebugLoggedForChatId === chatId) return;
  chatEnvDebugLoggedForChatId = chatId;

  const collectionName = getChatCollectionName(kind);
  console.log('[ChatEnvDebug] chat kind', { chatId, kind, collectionName });

  console.log('[ChatEnvDebug] firebase config', {
    projectId: firebase.options.projectId,
    appId: firebase.options.appId,
    authDomain: firebase.options.authDomain,
  });

  const versionInfo = await getInstalledAppVersion().catch(() => null);
  console.log('[ChatEnvDebug] build info', versionInfo);

  const user = auth.currentUser;
  console.log('[ChatEnvDebug] auth user', {
    uid: user?.uid,
    email: user?.email,
    isAnonymous: user?.isAnonymous,
    emailVerified: user?.emailVerified,
  });

  if (!user) {
    console.warn('[ChatEnvDebug] no authenticated user — skipping token/profile/network checks');
    return;
  }

  try {
    await user.getIdToken(true);
    console.log('[ChatEnvDebug] id token force-refresh succeeded');
  } catch (error: any) {
    console.error('[ChatEnvDebug] id token force-refresh FAILED', { code: error?.code, message: error?.message });
  }

  try {
    const [userSnap, partnerSnap] = await Promise.all([
      getDoc(doc(firestore, FIRESTORE_COLLECTIONS.users, user.uid)),
      getDoc(doc(firestore, 'partnerProfiles', user.uid)).catch(() => null),
    ]);
    console.log('[ChatEnvDebug] user doc', {
      profileExists: userSnap.exists(),
      role: userSnap.data()?.role,
      accountStatus: userSnap.data()?.accountStatus ?? userSnap.data()?.status,
      partnerStatus: partnerSnap?.exists() ? partnerSnap.data()?.status : undefined,
    });
  } catch (error: any) {
    console.error('[ChatEnvDebug] user doc read FAILED', { code: error?.code, message: error?.message });
  }

  const probeStart = Date.now();
  try {
    await getDocFromServer(doc(firestore, FIRESTORE_COLLECTIONS.users, user.uid));
    console.log('[ChatEnvDebug] authoritative server read succeeded', { elapsedMs: Date.now() - probeStart });
  } catch (error: any) {
    console.error('[ChatEnvDebug] authoritative server read FAILED', {
      elapsedMs: Date.now() - probeStart,
      code: error?.code,
      message: error?.message,
    });
  }
}

export async function sendChatMessage({
  chatId,
  kind,
  text,
  senderId,
  senderName,
  senderPhotoURL,
  sharedEvent,
  sharedVenue,
  imageUrl,
  gif,
  audioUrl,
  audioStoragePath,
  audioMimeType,
  duration,
  waveformData,
  replyTo,
}: {
  chatId: string;
  kind: ChatItem['kind'];
  text: string;
  senderId: string;
  senderName: string;
  senderPhotoURL?: string;
  sharedEvent?: {
    eventId: string;
    title: string;
    location: string;
    time: string;
    teaser?: string;
    imageUrl?: string;
    link?: string;
    endAt?: string;
  };
  sharedVenue?: {
    venueId: string;
    name: string;
    category: string;
    coverUrl?: string;
    city?: string;
    lat?: number;
    lng?: number;
    address?: string;
  };
  imageUrl?: string;
  gif?: {
    id: string;
    title: string;
    url: string;
    previewUrl: string;
    width: number;
    height: number;
    provider: 'giphy' | 'tenor' | string;
  };
  audioUrl?: string;
  audioStoragePath?: string;
  /** MIME type the recorder actually produced (e.g. 'audio/mp4', 'audio/webm;codecs=opus') — lets any receiving client (web or native) decide, before attempting playback, whether it can decode this specific message. Absent on legacy pre-Phase-4 voice messages, which are always 'audio/mp4' (native's only historical output format). */
  audioMimeType?: string;
  duration?: number;
  waveformData?: number[];
  replyTo?: { messageId: string; senderName: string; text: string };
}): Promise<void> {
  assertSocialWriteAllowed();
  const collectionName = getChatCollectionName(kind);

  const createdAt = serverTimestamp();
  const type = sharedEvent ? 'shared_event' : sharedVenue ? 'shared_venue' : imageUrl ? 'image' : gif ? 'gif' : audioUrl ? 'voice' : 'text';
  // [ChatSendDebug] TEMPORARY — remove once the real-device silent-message-loss
  // bug is confirmed fixed. Logs both write targets and the batch result so a
  // device log can show whether commit() actually throws, or resolves
  // successfully while the message never reaches the server.
  const messageRef = doc(collection(firestore, collectionName, chatId, 'messages'));
  const chatDocRef = doc(firestore, collectionName, chatId);
  if (__DEV__) console.log('[ChatSendDebug] sendChatMessage start', {
    senderId,
    chatId,
    kind,
    collectionName,
    messageId: messageRef.id,
    messagePath: messageRef.path,
    chatDocPath: chatDocRef.path,
    type,
    textLength: text?.length ?? 0,
    hasReplyTo: Boolean(replyTo),
    usesBatch: true,
  });
  const messageDoc: Record<string, unknown> = {
    text,
    type,
    eventId: sharedEvent?.eventId || '',
    senderId,
    senderName,
    senderPhotoURL: senderPhotoURL || '',
    createdAt,
  };
  if (sharedEvent) messageDoc.sharedEvent = sharedEvent;
  if (sharedVenue) messageDoc.sharedVenue = sharedVenue;
  if (imageUrl) messageDoc.imageUrl = imageUrl;
  if (gif) messageDoc.gif = gif;
  if (audioUrl) {
    messageDoc.audioUrl = audioUrl;
    messageDoc.audioStoragePath = audioStoragePath || '';
    messageDoc.audioMimeType = audioMimeType || 'audio/mp4';
    messageDoc.duration = duration || 0;
    messageDoc.waveformData = waveformData || [];
    messageDoc.status = 'sent';
  }
  if (replyTo) messageDoc.replyTo = replyTo;

  const lastMessageText = sharedEvent
    ? `Shared an event: ${sharedEvent.title}`
    : sharedVenue
      ? `Shared a venue: ${sharedVenue.name}`
      : imageUrl
        ? '📷 Photo'
        : gif
          ? 'GIF'
          : audioUrl
            ? '🎤 Voice message'
            : text;
  const batch = writeBatch(firestore);
  batch.set(messageRef, messageDoc);
  batch.set(
    chatDocRef,
    {
      lastMessage: lastMessageText,
      lastMessageAt: createdAt,
      lastActivityAt: createdAt,
      lastMessageSenderId: senderId,
      lastMessageSenderName: senderName,
      [`unreadCountByUser.${senderId}`]: 0,
    },
    { merge: true },
  );
  // [ChatSendDebug] TEMPORARY — see note above. Deliberately NOT swallowed:
  // logs the full Firestore error (code/message) and re-throws unchanged so
  // existing callers' try/catch behavior is unaffected.
  try {
    await batch.commit();
    if (__DEV__) console.log('[ChatSendDebug] sendChatMessage batch.commit() resolved', {
      senderId,
      chatId,
      messageId: messageRef.id,
    });
  } catch (error: any) {
    console.error('[ChatSendDebug] sendChatMessage batch.commit() FAILED', {
      senderId,
      chatId,
      messageId: messageRef.id,
      messagePath: messageRef.path,
      chatDocPath: chatDocRef.path,
      code: error?.code,
      message: error?.message,
      error,
    });
    throw error;
  }
}

/** Live per-user read timestamps off the parent chat doc — powers per-bubble "Seen" receipts. */
export function useChatLastReadAt(chatId: string | null, kind: ChatItem['kind']) {
  const [lastReadAt, setLastReadAt] = useState<Record<string, any>>({});

  useEffect(() => {
    if (!chatId) {
      setLastReadAt({});
      return undefined;
    }
    const unsub = onSnapshot(
      doc(firestore, getChatCollectionName(kind), chatId),
      snapshot => {
        const data = snapshot.data();
        setLastReadAt(data?.lastReadAt && typeof data.lastReadAt === 'object' ? data.lastReadAt : {});
      },
      error => {
        console.warn('[Tonyte][RN][ChatReads] lastReadAt subscription failed', { chatId, kind, error });
      },
    );
    return unsub;
  }, [chatId, kind]);

  return lastReadAt;
}

// Detects the parent chat doc disappearing out from under an open
// ChatDetailScreen — the only way that happens today is a Vibe being ended
// (unvibeUser fully deletes privateChats/{chatId}; see functions/index.js),
// since every other kind only ever gets a per-user `deletedFor` flag, never
// a real delete. `exists` starts `true` so a chat that's still loading its
// first snapshot never flashes the "ended" state before it's had a chance
// to prove otherwise.
export function useChatDocExists(chatId: string | null, kind: ChatItem['kind']): boolean {
  const [exists, setExists] = useState(true);

  useEffect(() => {
    if (!chatId) {
      setExists(true);
      return undefined;
    }
    setExists(true);
    const unsub = onSnapshot(
      doc(firestore, getChatCollectionName(kind), chatId),
      snapshot => setExists(snapshot.exists()),
      error => {
        console.warn('[Tonyte][RN][Chat] chat-existence subscription failed', { chatId, kind, error });
      },
    );
    return unsub;
  }, [chatId, kind]);

  return exists;
}

export async function markChatReadForUser({
  chatId,
  kind,
  userId,
}: {
  chatId: string;
  kind: ChatItem['kind'];
  userId: string;
}): Promise<void> {
  await setDoc(
    doc(firestore, FIRESTORE_COLLECTIONS.users, userId, 'chatReads', chatId),
    {
      chatId,
      kind,
      lastReadAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  );

  // Also stamp the reader's read time onto the parent chat doc (readable by
  // every other member already) so bubbles can render real per-user "Seen"
  // receipts without a new collection or per-message writes. Best-effort —
  // the chatReads write above is the source of truth for unread counts; this
  // one is purely for the read-receipt UI, so a failure here shouldn't be
  // treated as the read-marking having failed. Deliberately `updateDoc`,
  // not `setDoc(..., {merge:true})`: the latter silently *recreates* the
  // parent chat doc if it no longer exists (e.g. a Vibe that just got
  // ended by the other side, or an event/plan chat deleted from under a
  // stale mounted screen) — an `update` on a missing doc fails instead,
  // which is exactly what should happen here.
  try {
    await updateDoc(
      doc(firestore, getChatCollectionName(kind), chatId),
      { [`lastReadAt.${userId}`]: serverTimestamp() },
    );
  } catch (error) {
    console.warn('[Tonyte][RN][ChatReads] failed to stamp lastReadAt on chat doc', { chatId, kind, userId, error });
  }
}

export async function deleteChatForUser({
  chatId,
  kind,
  userId,
}: {
  chatId: string;
  kind: ChatItem['kind'];
  userId: string;
}): Promise<void> {
  const collectionName =
    kind === 'community'
      ? FIRESTORE_COLLECTIONS.partnerCommunityChats
      : kind === 'event'
      ? FIRESTORE_COLLECTIONS.eventChats
      : kind === 'plan'
      ? FIRESTORE_COLLECTIONS.planChats
      : kind === 'vibe'
      ? FIRESTORE_COLLECTIONS.privateChats
      : FIRESTORE_COLLECTIONS.matches;

  await updateDoc(doc(firestore, collectionName, chatId), {
    [`deletedFor.${userId}`]: true,
    [`deletedAtFor.${userId}`]: serverTimestamp(),
  });
}

export async function loadProfileOnce(uid: string) {
  const snapshot = await getDoc(doc(firestore, FIRESTORE_COLLECTIONS.users, uid));
  return normalizeProfile(uid, snapshot.exists() ? snapshot.data() : {});
}

// ─── Vibe Request hooks and helpers ─────────────────────────────────────────

export function useIncomingVibeRequests(uid: string | null): { requests: VibeRequest[]; loading: boolean } {
  const [requests, setRequests] = useState<VibeRequest[]>([]);
  const [loading, setLoading] = useState(Boolean(uid));

  useEffect(() => {
    if (!uid) {
      setRequests([]);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    const q = query(
      collection(firestore, FIRESTORE_COLLECTIONS.vibeRequests),
      where('toUserId', '==', uid),
      where('status', '==', 'pending'),
    );

    const unsub = onSnapshot(
      q,
      snapshot => {
        setRequests(snapshot.docs.map(d => {
          const data = d.data();
          return {
            id: d.id,
            fromUserId: String(data.fromUserId ?? ''),
            toUserId: String(data.toUserId ?? ''),
            sharedChatId: String(data.sharedChatId ?? ''),
            sharedChatKind: data.sharedChatKind === 'profile'
              ? 'profile' as const
              : data.sharedChatKind === 'plan'
                ? 'plan' as const
                : 'event' as const,
            status: 'pending' as const,
            createdAt: toDateString(data.createdAt),
            updatedAt: toDateString(data.updatedAt),
            message: firstString(data.message),
            sourceType: firstString(data.sourceType),
            sourceId: firstString(data.sourceId),
            sourceTitle: firstString(data.sourceTitle, data.sharedChatTitle),
            privateChatId: firstString(data.privateChatId),
          };
        }));
        setLoading(false);
      },
      () => {
        setRequests([]);
        setLoading(false);
      },
    );

    return unsub;
  }, [uid]);

  return { requests, loading };
}

export function useSentVibeRequests(uid: string | null): { requests: VibeRequest[]; loading: boolean } {
  const [requests, setRequests] = useState<VibeRequest[]>([]);
  const [loading, setLoading] = useState(Boolean(uid));

  useEffect(() => {
    if (!uid) {
      setRequests([]);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    const q = query(
      collection(firestore, FIRESTORE_COLLECTIONS.vibeRequests),
      where('fromUserId', '==', uid),
    );

    const unsub = onSnapshot(
      q,
      snapshot => {
        const nextRequests = snapshot.docs.map(d => {
          const data = d.data();
          const status = data.status === 'accepted'
            ? 'accepted' as const
            : data.status === 'declined'
              ? 'declined' as const
              : 'pending' as const;
          return {
            id: d.id,
            fromUserId: String(data.fromUserId ?? ''),
            toUserId: String(data.toUserId ?? ''),
            sharedChatId: String(data.sharedChatId ?? ''),
            sharedChatKind: data.sharedChatKind === 'profile'
              ? 'profile' as const
              : data.sharedChatKind === 'plan'
                ? 'plan' as const
                : 'event' as const,
            status,
            createdAt: toDateString(data.createdAt),
            updatedAt: toDateString(data.updatedAt),
            message: firstString(data.message),
            sourceType: firstString(data.sourceType),
            sourceId: firstString(data.sourceId),
            sourceTitle: firstString(data.sourceTitle, data.sharedChatTitle),
            privateChatId: firstString(data.privateChatId),
          };
        }).sort((left, right) => getChatTimeMs(right.updatedAt || right.createdAt) - getChatTimeMs(left.updatedAt || left.createdAt));
        setRequests(nextRequests);
        setLoading(false);
      },
      () => {
        setRequests([]);
        setLoading(false);
      },
    );

    return unsub;
  }, [uid]);

  return { requests, loading };
}

export async function sendVibeRequest({
  fromUserId,
  toUserId,
  sharedChatId = 'profile',
  sharedChatKind = 'profile',
  message,
}: {
  fromUserId: string;
  toUserId: string;
  sharedChatId?: string;
  sharedChatKind?: 'event' | 'plan' | 'profile';
  message?: string;
}): Promise<void> {
  assertSocialWriteAllowed();
  const requestId = `${fromUserId}_${toUserId}`;
  const pairKey = getVibePairKey(fromUserId, toUserId);
  const [relationshipSnap, sentSnap, receivedSnap] = await Promise.all([
    getDoc(doc(firestore, FIRESTORE_COLLECTIONS.vibeRelationships, pairKey)).catch(() => null),
    getDoc(doc(firestore, FIRESTORE_COLLECTIONS.vibeRequests, requestId)).catch(() => null),
    getDoc(doc(firestore, FIRESTORE_COLLECTIONS.vibeRequests, `${toUserId}_${fromUserId}`)).catch(() => null),
  ]);
  if (relationshipSnap?.exists() && relationshipSnap.data().status === 'active') {
    throw new Error('Already vibing.');
  }
  const sentStatus = sentSnap?.exists() ? sentSnap.data().status : '';
  const receivedStatus = receivedSnap?.exists() ? receivedSnap.data().status : '';
  if (sentStatus === 'pending' || sentStatus === 'accepted' || receivedStatus === 'pending' || receivedStatus === 'accepted') {
    throw new Error('A vibe request already exists.');
  }
  await Promise.all([
    sentStatus === 'declined'
      ? deleteDoc(doc(firestore, FIRESTORE_COLLECTIONS.vibeRequests, requestId))
      : Promise.resolve(),
    receivedStatus === 'declined'
      ? deleteDoc(doc(firestore, FIRESTORE_COLLECTIONS.vibeRequests, `${toUserId}_${fromUserId}`))
      : Promise.resolve(),
  ]);

  const sourceTitle = sharedChatKind === 'profile'
    ? 'Tonyte profile'
    : await getDoc(doc(
        firestore,
        sharedChatKind === 'plan' ? FIRESTORE_COLLECTIONS.planChats : FIRESTORE_COLLECTIONS.eventChats,
        sharedChatId,
      ))
        .then(snapshot => {
          if (!snapshot.exists()) return '';
          const data = snapshot.data();
          return sharedChatKind === 'plan'
            ? firstString(data.planTitle, data.name)
            : firstString(data.eventTitle, data.name);
        })
        .catch(() => '');
  const trimmedMessage = firstString(message);
  const payload = {
    fromUserId,
    toUserId,
    sharedChatId,
    sharedChatKind,
    sourceType: sharedChatKind,
    sourceId: sharedChatId,
    sourceTitle,
    ...(trimmedMessage ? { message: trimmedMessage } : {}),
    status: 'pending' as const,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  const requestPath = `${FIRESTORE_COLLECTIONS.vibeRequests}/${requestId}`;

  console.info('[Tonyte][RN][Vibe] creating request', {
    'currentUser.uid': fromUserId,
    selectedUserId: toUserId,
    sharedChatId,
    sharedChatKind,
    vibeRequestPayload: payload,
    firestoreDocumentPath: requestPath,
  });

  await setDoc(doc(firestore, FIRESTORE_COLLECTIONS.vibeRequests, requestId), payload);
}

// Accepting a Vibe request must produce exactly one active private chat for
// the pair, even if this fires twice concurrently (e.g. a double-tap, or two
// devices signed into the same account). `vibeRelationships/{pairKey}` is
// the single authoritative lock: the transaction below only ever mints a
// new `privateChats` doc (with a fresh, never-reused id — see
// getVibePairKey's comment) when no active relationship already exists for
// this pair; a concurrent/duplicate accept instead just reuses the winner's
// activeChatId. Firestore transactions on the same document are
// serialized by the backend and the client SDK retries on contention, so
// this is race-free without any additional locking.
export async function acceptVibeRequest({
  requestId,
  fromUserId,
  toUserId,
}: {
  requestId: string;
  fromUserId: string;
  toUserId: string;
}): Promise<string> {
  assertSocialWriteAllowed();
  const pairKey = getVibePairKey(fromUserId, toUserId);
  const sortedUsers = [fromUserId, toUserId].sort();
  const relationshipRef = doc(firestore, FIRESTORE_COLLECTIONS.vibeRelationships, pairKey);

  const chatId = await runTransaction(firestore, async tx => {
    const relationshipSnap = await tx.get(relationshipRef);
    if (relationshipSnap.exists() && relationshipSnap.data().status === 'active') {
      return String(relationshipSnap.data().activeChatId);
    }

    const newChatRef = doc(collection(firestore, FIRESTORE_COLLECTIONS.privateChats));
    tx.set(relationshipRef, {
      users: sortedUsers,
      status: 'active',
      activeChatId: newChatRef.id,
      fromVibeRequestId: requestId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    tx.set(newChatRef, {
      users: sortedUsers,
      kind: 'vibe',
      vibeRelationshipId: pairKey,
      createdAt: serverTimestamp(),
      lastMessage: '',
      lastMessageAt: serverTimestamp(),
      lastMessageSenderId: '',
    });
    return newChatRef.id;
  });

  await updateDoc(doc(firestore, FIRESTORE_COLLECTIONS.vibeRequests, requestId), {
    status: 'accepted',
    privateChatId: chatId,
    updatedAt: serverTimestamp(),
  });
  return chatId;
}

export async function declineVibeRequest(requestId: string): Promise<void> {
  await updateDoc(doc(firestore, FIRESTORE_COLLECTIONS.vibeRequests, requestId), {
    status: 'declined',
    updatedAt: serverTimestamp(),
  });
}

export async function cancelVibeRequest(requestId: string): Promise<void> {
  await deleteDoc(doc(firestore, FIRESTORE_COLLECTIONS.vibeRequests, requestId));
}
